import React, { useRef, useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Rnd } from "react-rnd";
import {
  clampToCanvas,
  clampSize,
  getToolConstraints,
  getToolOverflow,
  validateMove,
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  SIGNATURE_LABEL_OFFSET,
} from "../utils/canvasConstraints";

const DragTool = ({
  isEditMode = false,
  enableResizing = true,
  bgColor = "rgb(28,95,156, 0.5)",
  signatoryLabel = "",
  toolType,
  toolIcon,
  eleId,
  pageId,
  x,
  y,
  width,
  height,
  minWidth,
  minHeight,
  maxHeight,
  maxWidth,
  handleDragStart,
  handleDragStop,
  handleResize,
  iconPlacement = "flex-start",
  allCoordinates = [],
  hasError = false,
}) => {
  const pageDimensions = useSelector((state) => state.generalDocument?.pageDimensions);
  const canvasWidth = pageDimensions?.width || CANVAS_WIDTH;
  const canvasHeight = pageDimensions?.height || CANVAS_HEIGHT;

  const [isOverlapping, setIsOverlapping] = useState(false);
  const [resizeBlocked, setResizeBlocked] = useState(false);
  const dragStartPos = useRef({ x, y });
  const lastValidPos = useRef({ x, y });
  const currentPos = useRef({ x, y });
  const allCoordinatesRef = useRef(allCoordinates);
  const lastValidSize = useRef({ width, height, x, y });

  useEffect(() => {
    currentPos.current = { x, y };
    lastValidPos.current = { x, y };
  }, [x, y]);

  useEffect(() => {
    lastValidSize.current = { width, height, x, y };
  }, [width, height, x, y]);

  useEffect(() => {
    allCoordinatesRef.current = allCoordinates;
  }, [allCoordinates]);

  /**
   * Check if a proposed rectangle overlaps any other tool on the same page.
   * Reuses the centralized utility via the ref-based coordinate list.
   */
  const checkOverlap = useCallback(
    (newX, newY, newWidth = width, newHeight = height) => {
      let newRect = { x: newX, y: newY, width: newWidth, height: newHeight };
      // For signature tools, expand bounding box to include labels
      if (toolType === "Signature") {
        newRect = {
          x: newRect.x,
          y: newRect.y - SIGNATURE_LABEL_OFFSET,
          width: newRect.width,
          height: newRect.height + SIGNATURE_LABEL_OFFSET * 2,
        };
      }
      return !validateMove(newRect, allCoordinatesRef.current, eleId);
    },
    [eleId, width, height, toolType]
  );

  /**
   * Clamp position to canvas bounds for the current tool dimensions,
   * accounting for overflow elements (signature labels, picklist error messages).
   */
  const clampPosition = useCallback(
    (posX, posY, w = width, h = height) => {
      const overflow = getToolOverflow(toolType, { hasError });
      return clampToCanvas(
        { x: posX, y: posY, width: w, height: h },
        { width: canvasWidth, height: canvasHeight },
        overflow
      );
    },
    [width, height, toolType, hasError, canvasWidth, canvasHeight]
  );

  return (
    <Rnd
      className="drag-tool"
      style={{
        position: "absolute",
        display: "flex",
        alignItems: "center",
        justifyContent: iconPlacement,
        padding: "0px 10px",
        border: (isOverlapping || resizeBlocked) ? "2px solid #e53e3e" : hasError ? `2px dashed ${bgColor}` : "solid 1px #000000",
        background: (isOverlapping || resizeBlocked) ? "rgba(229, 62, 62, 0.35)" : `${bgColor}`,
        zIndex: 9999,
        cursor: isOverlapping ? "not-allowed" : "grab",
        outline: (isOverlapping || resizeBlocked) ? "2px dashed #e53e3e" : "none",
        transition: "border 0.1s, background 0.1s, outline 0.1s",
      }}
      enableResizing={enableResizing}
      size={{ width, height }}
      position={(() => {
        const overflow = getToolOverflow(toolType, { hasError });
        const clamped = clampToCanvas(
          { x, y, width, height },
          { width: canvasWidth, height: canvasHeight },
          overflow
        );
        return { x: clamped.x, y: clamped.y };
      })()}
      minWidth={minWidth}
      minHeight={minHeight}
      maxWidth={maxWidth}
      maxHeight={maxHeight}
      bounds="parent"
      onDragStart={(e, d) => {
        dragStartPos.current = { x: currentPos.current.x, y: currentPos.current.y };
        lastValidPos.current = { x: currentPos.current.x, y: currentPos.current.y };
        setIsOverlapping(false);
        handleDragStart(toolType, e);
      }}
      onDrag={(e, d) => {
        // Clamp to canvas bounds
        const clamped = clampPosition(d.x, d.y);
        const overlapping = checkOverlap(clamped.x, clamped.y);
        setIsOverlapping(overlapping);

        // Track last valid (non-overlapping, in-bounds) position
        if (!overlapping) {
          lastValidPos.current = { x: clamped.x, y: clamped.y };
        }
      }}
      onDragStop={(e, d) => {
        const clamped = clampPosition(d.x, d.y);
        const overlapping = checkOverlap(clamped.x, clamped.y);
        setIsOverlapping(false);

        if (overlapping) {
          // Revert to last known valid position (better UX than jumping to start)
          handleDragStop(eleId, {
            x: lastValidPos.current.x,
            y: lastValidPos.current.y,
          });
        } else {
          handleDragStop(eleId, { x: clamped.x, y: clamped.y });
        }
      }}
      onResize={(e, direction, ref, delta, position) => {
        // Real-time resize validation
        const rawWidth = parseInt(ref.style.width, 10);
        const rawHeight = parseInt(ref.style.height, 10);

        // Clamp to min/max size constraints
        const sizeConstraints = getToolConstraints(toolType);
        const sized = clampSize(rawWidth, rawHeight, sizeConstraints);

        // Clamp to canvas (with overflow for labels/errors)
        const overflow = getToolOverflow(toolType, { hasError });
        const clamped = clampToCanvas(
          { x: position.x, y: position.y, width: sized.width, height: sized.height },
          { width: canvasWidth, height: canvasHeight },
          overflow
        );

        const overlapping = checkOverlap(clamped.x, clamped.y, clamped.width, clamped.height);

        if (overlapping) {
          setResizeBlocked(true);
        } else {
          setResizeBlocked(false);
          // Track last valid resize state
          lastValidSize.current = {
            width: clamped.width,
            height: clamped.height,
            x: clamped.x,
            y: clamped.y,
          };
        }
      }}
      onResizeStop={(e, direction, ref, delta, position) => {
        const rawWidth = parseInt(ref.style.width, 10);
        const rawHeight = parseInt(ref.style.height, 10);

        // Clamp to min/max size constraints
        const sizeConstraints = getToolConstraints(toolType);
        const sized = clampSize(rawWidth, rawHeight, sizeConstraints);

        // Clamp to canvas (with overflow for labels/errors)
        const overflow = getToolOverflow(toolType, { hasError });
        const clamped = clampToCanvas(
          { x: position.x, y: position.y, width: sized.width, height: sized.height },
          { width: canvasWidth, height: canvasHeight },
          overflow
        );

        const overlapping = checkOverlap(clamped.x, clamped.y, clamped.width, clamped.height);

        setResizeBlocked(false);

        if (overlapping) {
          // Revert to last valid size/position
          handleResize(
            {
              width: lastValidSize.current.width,
              height: lastValidSize.current.height,
              x: lastValidSize.current.x,
              y: lastValidSize.current.y,
            },
            e
          );
        } else {
          handleResize(
            {
              width: clamped.width,
              height: clamped.height,
              x: clamped.x,
              y: clamped.y,
            },
            e
          );
        }
      }}
    >
      <div style={{ width: "100%", height: "100%", cursor: isOverlapping ? "not-allowed" : "inherit" }}>
        {isEditMode && (
          <>
            <div className="resize-circle tl-resize"></div>
            <div className="resize-circle tr-resize"></div>
            <div className="resize-circle br-resize"></div>
            <div className="resize-circle bl-resize"></div>
          </>
        )}
        {(isOverlapping || resizeBlocked) && (
          <div
            style={{
              position: "absolute",
              top: "-28px",
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "#e53e3e",
              color: "#fff",
              fontSize: "11px",
              fontWeight: 600,
              padding: "2px 8px",
              borderRadius: "4px",
              whiteSpace: "nowrap",
              pointerEvents: "none",
              zIndex: 10001,
              boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
            }}
          >
            Overlapping input
          </div>
        )}
        {hasError && !isOverlapping && !resizeBlocked && (
          <div
            style={{
              position: "absolute",
              bottom: "-22px",
              left: "50%",
              transform: "translateX(-50%)",
              color: "#c53030",
              fontSize: "10px",
              fontWeight: 600,
              whiteSpace: "nowrap",
              pointerEvents: "none",
              zIndex: 10001,
              backgroundColor: "rgba(255, 255, 255, 0.92)",
              padding: "1px 6px",
              borderRadius: "3px",
              border: "1px solid rgba(229, 62, 62, 0.3)",
              textShadow: "0 0 2px rgba(255,255,255,1)",
              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
            }}
          >
            ⚠ Add at least one option
          </div>
        )}
        {signatoryLabel && (
          <span
            style={{
              position: "absolute",
              top: "2px",
              right: "4px",
              fontSize: "9px",
              fontWeight: 600,
              color: "#333",
              pointerEvents: "none",
              userSelect: "none",
              whiteSpace: "nowrap",
              letterSpacing: "0.3px",
              backgroundColor: "rgba(255, 255, 255, 0.75)",
              backdropFilter: "blur(2px)",
              WebkitBackdropFilter: "blur(2px)",
              padding: "0px 4px",
              borderRadius: "2px",
              textShadow: "0 0 2px rgba(255,255,255,0.8)",
              lineHeight: "14px",
            }}
          >
            {signatoryLabel}
          </span>
        )}
        {toolIcon}
      </div>
    </Rnd>
  );
};

export default DragTool;